import {IconButton, TableCell, TableRow, Tooltip} from "@mui/material";
import {CheckIcon, CloseIcon, DeleteIcon, EmailIcon, OwnerIcon} from "../../icons";
import {ConfirmDialog} from "@dvrt/graphql-widgets";
import {HasPermission} from "../../widgets";
import {FormattedMessage} from "react-intl";
import {UserLogin} from "@/api";
import {CONFIRM_MESSAGES} from "@/utils/translations";
import {Permission} from "@/authSchema";
import {ContextFilter} from "@/context/AuthProvider";

type Props = {
    user: UserLogin;

    /** Permission required to delete login, make owner, resend email */
    actionsPermission: {name: Permission; contextFilter: ContextFilter};

    deleteUser: () => void;
    makeOwner: () => void;
    resendEmail: () => void;
    openDeleteUserDialog: () => void;
    openMakeOwnerDialog: () => void;
    openResendInvitationEmailDialog: () => void;
    closeDeleteUserDialog: () => void;
    closeMakeOwnerDialog: () => void;
    closeResendInvitationEmailDialog: () => void;

    isDeleteUserDialogOpen: boolean;
    isMakeOwnerDialogOpen: boolean;
    isResendInvitationEmailDialogOpen: boolean;
    changeOwnerDisabled: boolean;
};

export default function SharedRow({
    user,
    actionsPermission,
    isDeleteUserDialogOpen,
    isMakeOwnerDialogOpen,
    isResendInvitationEmailDialogOpen,
    deleteUser,
    makeOwner,
    resendEmail,
    openDeleteUserDialog,
    openMakeOwnerDialog,
    openResendInvitationEmailDialog,
    closeDeleteUserDialog,
    closeMakeOwnerDialog,
    closeResendInvitationEmailDialog,
    changeOwnerDisabled,
}: Props) {
    function concat(items: string[]): string {
        let result = items[0];
        for (let i = 1; i < items.length; i++) {
            result += `, ${items[i]}`;
        }
        return result;
    }

    return (
        <TableRow>
            <TableCell>{user.name}</TableCell>
            <TableCell>{concat(user.loginNames)}</TableCell>
            <TableCell>{concat(user.emails)}</TableCell>
            <TableCell>{changeOwnerDisabled ? <CheckIcon /> : <CloseIcon />}</TableCell>
            <HasPermission {...actionsPermission}>
                <TableCell align="right">
                    <Tooltip
                        title={
                            <FormattedMessage id="userManagement.tooltips.deleteUser" defaultMessage="Delete login" />
                        }
                        arrow
                        placement="top"
                    >
                        <IconButton color="secondary" onClick={() => openDeleteUserDialog()} size="small">
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="userManagement.tooltips.makeOwner"
                                defaultMessage="Make login the owner of the user"
                            />
                        }
                        arrow
                        placement="top"
                    >
                        <IconButton
                            color="primary"
                            onClick={() => openMakeOwnerDialog()}
                            size="small"
                            disabled={changeOwnerDisabled}
                        >
                            <OwnerIcon />
                        </IconButton>
                    </Tooltip>
                    {!user.isVerified && (
                        <Tooltip
                            title={
                                <FormattedMessage
                                    id="userManagement.tooltips.resendInvitationEmail"
                                    defaultMessage="Resend the invitation email"
                                />
                            }
                            arrow
                            placement="top"
                        >
                            <IconButton color="primary" onClick={() => openResendInvitationEmailDialog()} size="small">
                                <EmailIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
            </HasPermission>
            <ConfirmDialog
                isOpen={isDeleteUserDialogOpen}
                close={closeDeleteUserDialog}
                onConfirm={deleteUser}
                message={CONFIRM_MESSAGES.delete_extension_user}
                messageVars={{
                    user: user.emails[0] || user.loginNames[0] || user.name,
                }}
                buttonIdPrefix="delete-extension-user"
            />
            <ConfirmDialog
                isOpen={isMakeOwnerDialogOpen}
                close={closeMakeOwnerDialog}
                onConfirm={makeOwner}
                message={CONFIRM_MESSAGES.make_owner}
                messageVars={{
                    login: user.emails[0] || user.loginNames[0] || user.name,
                }}
                buttonIdPrefix="make-owner"
            />
            <ConfirmDialog
                isOpen={isResendInvitationEmailDialogOpen}
                close={closeResendInvitationEmailDialog}
                onConfirm={resendEmail}
                message={CONFIRM_MESSAGES.resendInvitationEmail}
                messageVars={{
                    user: user.emails[0] || user.loginNames[0] || user.name,
                }}
                buttonIdPrefix="resend-email"
            />
        </TableRow>
    );
}
